<script lang="ts">
  import type {INotification} from "$lib/interfaces/INotification";
  import TimeAgo from "$lib/elements/TimeAgo.svelte";
  import {goto} from "$app/navigation";
  import {locale} from "$lib/i18n";
  import {createEventDispatcher} from "svelte";
  import {openDialog} from "$lib/stores/dialogStores";

  interface Props {
    notification: INotification;
    showCloseButton?: boolean;
  }

  let {
    notification,
    showCloseButton = $bindable(true)
  }: Props = $props();

  const dispatch = createEventDispatcher();


  const currencyFullNames = {
    'btc': 'Bitcoin',
    'eth': 'Ethereum',
    'ltc': 'Litecoin',
    'xrp': 'Ripple',
    'bch': 'Bitcoin Cash',
    'usdt': 'Tether',
    'usdte': 'Tether (ERC20)',
    'usdtt': 'Tether (TRC20)',
    'doge': 'Dogecoin',
    'trx': 'Tron',
    'usdc': 'USD Coin'
  };


  const currencyImageMap = {
    'btc': 'bitcoin',
    'bitcoin': 'bitcoin',
    'eth': 'ethereum',
    'ethereum': 'ethereum',
    'ltc': 'litecoin',
    'litecoin': 'litecoin',
    'xrp': 'ripple',
    'ripple': 'ripple',
    'bch': 'bitcoincash',
    'bitcoincash': 'bitcoincash',
    'bitcoin cash': 'bitcoincash',
    'usdt': 'usdt',
    'tether': 'usdt',
    'usdte': 'usdt',
    'usdtt': 'usdt',
    'doge': 'dogecoin',
    'dogecoin': 'dogecoin',
    'trx': 'trx',
    'tron': 'trx',
    'usdc': 'usdc'
  };


  function extractCurrencyInfo() {
    if (notification.type === 'DEPOSIT' || notification.type === 'DEPOSIT_PENDING') {

      const contentText = notification.content.replace(/<[^>]*>/g, '');


      const depositMatch = contentText.match(/deposited\s+([\d.]+)\s+([A-Za-z]+)(?:\s+to|$)/i);

      if (depositMatch) {
        const amount = depositMatch[1];
        const rawCurrency = depositMatch[2].trim();


        let currencyCode = rawCurrency.toLowerCase();


        if (currencyCode === 'btc' || currencyCode === 'bitc') {
          currencyCode = 'bitcoin';
        }

        const currencyFullName = currencyFullNames[currencyCode] ||
                (currencyCode === 'bitcoin' ? 'Bitcoin' : rawCurrency);


        return {
          amount: amount,
          currency: currencyCode,
          currencyFullName: currencyFullName,
          rawCurrency: rawCurrency
        };
      }
    }
    return null;
  }


  function getCurrencyIcon(currencyCode: string) {
    if (!currencyCode) return '';


    const code = currencyCode.toLowerCase();


    if (code === 'btc' || code === 'bitc') {
      return '/currencies/bitcoin.png';
    }


    const imageFilename = currencyImageMap[code] || code;

    return `/currencies/${imageFilename}.png`;
  }

  // Function to clean game slug by removing the "-mobile" suffix
  function cleanGameSlug(slug: string): string {
    if (!slug) return slug;

    // Check if the slug ends with "-mobile"
    if (slug.endsWith('-mobile')) {
      return slug.substring(0, slug.length - 7);
    }

    // Handle more complex cases where "-mobile" might be in the middle
    // For example: "gates-of-olympus-1000-mobile" should become "gates-of-olympus-1000"
    const parts = slug.split('-');
    if (parts.length > 1 && parts[parts.length - 1] === 'mobile') {
      return parts.slice(0, -1).join('-');
    }

    return slug;
  }

  const currencyInfo = extractCurrencyInfo();
</script>

<div
        class="flex rounded-xl bg-primary-dark border border-primary-blue/20 p-4 relative cursor-pointer hover:bg-primary-blue/10 transition-colors"
        on:click={() => {
    dispatch('click');
    if (notification.type === 'FREESPIN_REWARD' || notification.type === 'LEVEL_UP') {
      // Clean the game slug before navigation
      const cleanedSlug = cleanGameSlug(notification.data.game.slug);
      goto(`/${$locale}/game/${cleanedSlug}`);
    } else if ((notification.type === 'DEPOSIT' || notification.type === 'DEPOSIT_PENDING') && notification.data?.transactionOrderId) {
      openDialog('TransactionDetailsModal', notification.data.transactionOrderId);
    }
    dispatch('closeNotificationPopup');
  }}
>
  <div class="flex items-start w-full">
    <!-- Left side - Icon -->
    <div class="mr-3 mt-0.5">
      {#if notification.type === 'FREESPIN_REWARD' && notification.data?.game?.image}
        <div class="w-10 h-10 rounded-md overflow-hidden">
          <img src={notification.data.game.image || "/placeholder.svg"} class="w-full h-full object-cover" alt={notification.data.game?.name}>
        </div>
      {:else if (notification.type === 'DEPOSIT' || notification.type === 'DEPOSIT_PENDING') && currencyInfo}
        <!-- Currency icon for deposit notifications -->
        <div class="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-primary-blue/10">
          <img
                  src={getCurrencyIcon(currencyInfo.currency) || "/placeholder.svg"}
                  alt={currencyInfo.currencyFullName}
                  class="w-7 h-7 {notification.type === 'DEPOSIT_PENDING' ? 'opacity-70' : ''}"
          />
        </div>
      {:else if notification.type === 'BONUS'}
        <div class="w-10 h-10 bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-fire">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
          </svg>
        </div>
      {:else if notification.type === 'LEVEL_UP'}
        <div class="w-10 h-10 bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-fire">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
      {:else}
        <div class="w-10 h-10 bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-fire">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      {/if}
    </div>

    <!-- Right side - Content -->
    <div class="flex-1">
      <!-- Notification content -->
      <div class="text-white text-sm">
        {#if (notification.type === 'DEPOSIT' || notification.type === 'DEPOSIT_PENDING') && currencyInfo}
          <p>
            {notification.type === 'DEPOSIT' ? 'You have successfully deposited' : 'Your deposit of'}
            <span class="font-semibold">
              {currencyInfo.amount}
              <span class="text-primary-fire">{currencyInfo.currencyFullName}</span>
            </span>
            {notification.type === 'DEPOSIT' ? 'to your account.' : 'is pending confirmation.'}
          </p>
        {:else}
          <p>{@html notification.content}</p>
        {/if}

        {#if notification.data && notification.data?.game}
          <div class="mt-2 bg-primary-blue/10 p-2 rounded-md flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary-fire mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            <span>{notification.data.game?.name} - {notification.data?.freespinRewardCount} Free Spins</span>
          </div>
        {/if}
      </div>

      <!-- Timestamp -->
      <div class="text-xs text-primary-lightBlue mt-2">
        <TimeAgo date={notification.createdAt}/>
      </div>
    </div>

    {#if showCloseButton && !notification.viewed}
      <button
              class="absolute top-2 right-2 w-6 h-6 bg-primary-blue/20 rounded-full flex items-center justify-center hover:bg-primary-fire/20 transition-colors"
              on:click={(e) => {
          e.stopPropagation();
          dispatch('click');
        }}
              title="Mark as read"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary-fire" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    {/if}
  </div>
</div>